"use client";
import { useTranslations } from "next-intl";
import { Link } from "@/i18n/navigation";
import DonistaLogo from "@/components/icons/donista-logo";
import { getFooterNavigation, type FooterSection } from "@/lib/navigation-data";

interface Footer7Props {
    logo?: {
        url: string;
        src: string;
        alt: string;
        title: string;
    };
    extraSections?: FooterSection[];
}

const Footer7 = ({
    logo = {
        url: "donista.org",
        src: "https://shadcnblocks.com/images/block/logos/shadcnblockscom-icon.svg",
        alt: "logo",
        title: "donista.org",
    },
    extraSections,
}: Footer7Props) => {
    const t = useTranslations("common");
    const tNav = useTranslations("navigation");
    const sections: FooterSection[] = getFooterNavigation(tNav, extraSections ?? []);
    return (
        <footer className="pt-16 max-w-7xl mx-auto">
                    <div
                        className="flex flex-col items-start justify-between gap-10 text-center lg:flex-row lg:text-left">
                        <div
                            className="flex w-full max-w-96 shrink flex-col items-center justify-between gap-6 lg:items-start">
                            {/* Logo */}
                            <div className="flex items-center gap-2 lg:justify-start">
                                <DonistaLogo className="size-8"/>
                                <h2 className="text-xl font-semibold">{logo.title}</h2>
                            </div>
                            <p className="text-sm text-muted-foreground">
                                {t("footer.tagline")}
                            </p>

                        </div>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-12 text-left">
                            {sections.map((section, sectionIdx) => (
                                <div key={sectionIdx} className={section.wide ? "col-span-2" : undefined}>
                                    <h3 className="mb-3 font-bold">{section.title}</h3>
                                    <ul className={`text-sm text-muted-foreground ${section.wide ? "columns-2 gap-x-6 [&>li]:mb-2" : "space-y-2"}`}>
                                        {section.links.map((link, linkIdx) => (
                                            <li key={linkIdx} className="font-medium hover:text-primary break-inside-avoid">
                                                <Link href={link.href}>{link.name}</Link>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div
                        className="mt-10 flex flex-col justify-between gap-4 border-t py-8 text-center text-sm font-medium text-muted-foreground lg:flex-row lg:items-center lg:text-left">
                        <p>{t("footer.copyright")}</p>

                        <a href="https://logo.dev" className="hover:text-primary">{t("footer.logosAttribution")}</a>

                        <ul className="flex justify-center gap-4 lg:justify-start">
                            <li className="hover:text-primary">
                                <Link href="/legal/terms-of-service">{t("footer.terms")}</Link>
                            </li>
                            <li className="hover:text-primary">
                                <Link href="/legal/privacy">{t("footer.privacy")}</Link>
                            </li>
                            <li className="hover:text-primary">
                                <Link href="/imprint">{t("footer.imprint")}</Link>
                            </li>
                        </ul>
                    </div>
        </footer>
    );
};

export default Footer7;
